import { combineEpics } from 'redux-observable'
import { Observable } from 'rxjs/Observable'

import { URL_CONST } from 'utils/urlConsts'
import { matchURL } from 'utils/urls'

import { selectActualFeatureFlags } from 'containers/App/selectors'
import { FETCH_SELECTED_COMPANY_DATA_ASYNC } from 'containers/PartnerOptions/constants'
import {
  selectAllowedMspRoleIdFilter,
  selectAssignedCompaniesData,
  selectAssignedCompaniesMspRoleFilter,
  selectAssignedCompaniesPLanTypeFilter,
  selectAssignedCompaniesSearchFilter,
  selectAssignedSelectionData,
  selectIsToAssignDrawerOpen,
  selectManageableCompaniesData,
  selectSortProperty,
  selectToAssignCompaniesData,
  selectToAssignCompaniesSearchFilter,
  selectToAssignPlanTypeFilter,
  selectToAssignSelectionData,
  selectUserInfo,
  selectUserToUpdate,
} from 'containers/Settings/Users/selectors'
import { addToast } from 'containers/Toasts/actions'

import {
  addCompanyUserError,
  addCompanyUserSuccess,
  assignCompaniesError,
  assignCompaniesSuccess,
  delCompanyUserError,
  delCompanyUserSuccess,
  fetchCncAccessError,
  fetchCncAccessSuccess,
  fetchMailboxDataError,
  fetchMailboxDataSuccess,
  getCompanyUsersError,
  getCompanyUsersSuccess,
  getManageableCompaniesError,
  getManageableCompaniesSuccess,
  getManageableUserAssignedCompaniesError,
  getManageableUserAssignedCompaniesSuccess,
  getManageableUserToAssignCompaniesError,
  getManageableUserToAssignCompaniesSuccess,
  getProfilesError,
  getProfilesSuccess,
  getUserInfoError,
  getUserInfoSuccess,
  makeOwnerError,
  makeOwnerSuccess,
  unassignMSPCompaniesError,
  unassignMSPCompaniesSuccess,
  updateCncAccessError,
  updateCncAccessSuccess,
  updCompanyUserError,
  updCompanyUserSuccess,
} from './actions'
import {
  ADD_USER_REQUEST_ASYNC,
  ASSIGN_COMPANIES_ASYNC,
  ASSIGNED_COMPANIES_PER_PAGE,
  ASSIGNED_USER_COMPANIES_PER_PAGE,
  CHANGE_ALLOWED_MSP_ROLE_ID_FILTER,
  CHANGE_ASSIGNED_COMPANIES_MSP_ROLE_FILTER,
  CHANGE_ASSIGNED_COMPANIES_PLAN_TYPE_FILTER,
  CHANGE_ASSIGNED_COMPANIES_SEARCH_FILTER,
  CHANGE_TO_ASSIGN_COMPANIES_SEARCH_FILTER,
  CHANGE_TO_ASSIGN_PLAN_TYPE_FILTER,
  CNC_ACCESS_REQUEST_ASYNC,
  COMPANY_USERS_REQUEST_ASYNC,
  DATA_REQUEST_ASYNC,
  DELETE_USER_REQUEST_ASYNC,
  GET_MANAGEABLE_COMPANIES_ASYNC,
  GET_USER_INFO_ASYNC,
  GET_USER_MANAGEABLE_ASSIGNED_COMPANIES_ASYNC,
  GET_USER_MANAGEABLE_TO_ASSIGN_COMPANIES_ASYNC,
  MAKE_OWNER_ASYNC,
  PROFILES_REQUEST_ASYNC,
  SEARCH_CHANGE,
  UNASSIGN_COMPANIES_ASYNC,
  UPDATE_CNC_ACCESS_REQUEST_ASYNC,
  UPDATE_USER_REQUEST_ASYNC,
} from './constants'

const getCompanyUsersEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$
    .ofType(
      FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS,
      COMPANY_USERS_REQUEST_ASYNC.INITIATE,
      ADD_USER_REQUEST_ASYNC.SUCCESS,
      DELETE_USER_REQUEST_ASYNC.SUCCESS,
      UPDATE_USER_REQUEST_ASYNC.SUCCESS,
      MAKE_OWNER_ASYNC.SUCCESS
    )
    .filter((action) => {
      if (action.type === FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS)
        return matchURL(URL_CONST.settings.permissions)
      return true
    })
    .mergeMap(() => {
      let assignedCompaniesValue = selectManageableCompaniesData(store.getState()).value

      if (!assignedCompaniesValue.length) {
        assignedCompaniesValue = null
      }

      return api.settings
        .getCompanyUsers(
          store.getState().partnerOptions.selectedCompany?.value,
          selectSortProperty(store.getState()),
          assignedCompaniesValue
        )
        .map((response) => getCompanyUsersSuccess(response))
        .catch((error) => Observable.of(epicsErrorHandler(getCompanyUsersError, error)))
        .takeUntil(action$.ofType(COMPANY_USERS_REQUEST_ASYNC.CANCEL))
    })

const getProfilesEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$
    .ofType(
      FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS,
      PROFILES_REQUEST_ASYNC.INITIATE,
      ADD_USER_REQUEST_ASYNC.SUCCESS,
      DELETE_USER_REQUEST_ASYNC.SUCCESS,
      UPDATE_USER_REQUEST_ASYNC.SUCCESS
    )
    .filter((action) => {
      if (action.type === FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS)
        return matchURL(URL_CONST.settings.permissions)
      return true
    })
    .mergeMap(() =>
      api.settings
        .getProfiles(store.getState().partnerOptions.selectedCompany?.value)
        .map((response) => getProfilesSuccess(response.data))
        .catch((error) => Observable.of(epicsErrorHandler(getProfilesError, error)))
        .takeUntil(action$.ofType(PROFILES_REQUEST_ASYNC.CANCEL))
    )

const delCompanyUserEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(DELETE_USER_REQUEST_ASYNC.INITIATE).mergeMap(() =>
    api.settings
      .delCompanyUser(
        store.getState().partnerOptions.selectedCompany?.value,
        store.getState().settings.users.self.userToDelete
      )
      .mergeMap((response) =>
        Observable.of(
          delCompanyUserSuccess(response),
          addToast({
            message: 'User was deleted successfully',
          })
        )
      )
      .catch((error) =>
        Observable.of(
          epicsErrorHandler(delCompanyUserError, error),
          addToast({
            message: error.response?.message || error?.response,
            type: 'error',
          })
        )
      )
      .takeUntil(action$.ofType(DELETE_USER_REQUEST_ASYNC.CANCEL))
  )

const addCompanyUserEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(ADD_USER_REQUEST_ASYNC.INITIATE).mergeMap((action) =>
    api.settings
      .addCompanyUser(store.getState().partnerOptions.selectedCompany?.value, action.payload.user)
      .mergeMap((response) =>
        Observable.of(
          addCompanyUserSuccess(response),
          addToast({
            message: 'User was added successfully',
          })
        )
      )
      .catch((error) =>
        Observable.of(
          epicsErrorHandler(addCompanyUserError, error),
          addToast({
            message: error.response?.message || error?.response,
            type: 'error',
          })
        )
      )
      .takeUntil(action$.ofType(ADD_USER_REQUEST_ASYNC.CANCEL))
  )

const updCompanyUserEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(UPDATE_USER_REQUEST_ASYNC.INITIATE).mergeMap(() => {
    const userToUpdate = selectUserToUpdate(store.getState())
    // TODO: remove when we will get rid of snake_case
    const user = {
      //
      id: userToUpdate.id,
      email: userToUpdate.email,
      last_name: userToUpdate.lastName || userToUpdate.last_name,
      first_name: userToUpdate.firstName || userToUpdate.first_name,
      ioc_access: userToUpdate.iocAccess || userToUpdate.ioc_access,
      is_owner: userToUpdate.isOwner || userToUpdate.is_owner,
      role: userToUpdate.role,
      user_type: userToUpdate.userType || userToUpdate.user_type,
      company_id: userToUpdate.companyId || userToUpdate.company_id,
    }

    return api.settings
      .updCompanyUser(store.getState().partnerOptions.selectedCompany?.value, user)
      .mergeMap((response) =>
        Observable.of(
          updCompanyUserSuccess(response),
          addToast({
            message: 'User was updated successfully',
          })
        )
      )
      .catch((error) =>
        Observable.of(
          epicsErrorHandler(updCompanyUserError, error),
          addToast({
            message: error.response?.message || error?.response,
            type: 'error',
          })
        )
      )
      .takeUntil(action$.ofType(UPDATE_USER_REQUEST_ASYNC.CANCEL))
  })

const makeOwnerEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(MAKE_OWNER_ASYNC.INITIATE).mergeMap(() =>
    api.settings
      .makeOwner(
        store.getState().partnerOptions.selectedCompany?.value,
        store.getState().settings.users.self.userToUpdate
      )
      .mergeMap((response) =>
        Observable.of(
          makeOwnerSuccess(response),
          addToast({
            message: 'User was updated successfully',
          })
        )
      )
      .catch((error) =>
        Observable.of(
          epicsErrorHandler(makeOwnerError, error),
          addToast({
            message: error.response?.message || error?.response,
            type: 'error',
          })
        )
      )
      .takeUntil(action$.ofType(MAKE_OWNER_ASYNC.CANCEL))
  )

const fetchMailboxDataEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(SEARCH_CHANGE).mergeMap((action) =>
    api.mailboxes
      .get(
        undefined,
        '',
        undefined,
        undefined,
        '',
        '',
        '',
        action.payload.q,
        store.getState().partnerOptions.selectedCompany?.value
      )
      .map((response) => fetchMailboxDataSuccess(response))
      .catch((error) => Observable.of(epicsErrorHandler(fetchMailboxDataError, error, [400])))
      .takeUntil(action$.ofType(DATA_REQUEST_ASYNC.CANCEL))
  )

const fetchCncAccessDataEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$
    .ofType(
      CNC_ACCESS_REQUEST_ASYNC.INITIATE,
      UPDATE_CNC_ACCESS_REQUEST_ASYNC.SUCCESS,
      FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS
    )
    .filter((action) => {
      const ff = selectActualFeatureFlags(store.getState())
      if (!ff.DISABLE_CNC_ACCESS) return false
      if (action.type === FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS)
        return matchURL(URL_CONST.settings.permissions)
      return true
    })
    .mergeMap(() =>
      api.settings
        .getCncAccess(store.getState().partnerOptions.selectedCompany?.value)
        .map((response) => fetchCncAccessSuccess(response.cncAccess))
        .catch((error) => Observable.of(epicsErrorHandler(fetchCncAccessError, error, [400])))
        .takeUntil(action$.ofType(CNC_ACCESS_REQUEST_ASYNC.CANCEL))
    )

const setCncAccessDataEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(UPDATE_CNC_ACCESS_REQUEST_ASYNC.INITIATE).mergeMap((action) =>
    api.settings
      .setCncAccess(
        action.payload.cncAccess,
        store.getState().partnerOptions.selectedCompany?.value
      )
      .map(() => updateCncAccessSuccess())
      .catch((error) => Observable.of(epicsErrorHandler(updateCncAccessError, error, [400])))
      .takeUntil(action$.ofType(UPDATE_CNC_ACCESS_REQUEST_ASYNC.CANCEL))
  )

const getManageableCompaniesEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(GET_MANAGEABLE_COMPANIES_ASYNC.INITIATE).mergeMap((action) =>
    api.settings
      .getManageableCompanies({
        companyId: store.getState().partnerOptions.selectedCompany?.value,
        page: action.payload.params.page,
        pageSize: ASSIGNED_COMPANIES_PER_PAGE,
        search: action.payload.params.search,
      })
      .map((response) => getManageableCompaniesSuccess(response))
      .catch((error) => Observable.of(epicsErrorHandler(getManageableCompaniesError, error)))
      .takeUntil(action$.ofType(GET_MANAGEABLE_COMPANIES_ASYNC.CANCEL))
  )

const getUserInfoEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$
    .ofType(
      GET_USER_INFO_ASYNC.INITIATE,
      ASSIGN_COMPANIES_ASYNC.SUCCESS,
      UNASSIGN_COMPANIES_ASYNC.SUCCESS
    )
    .mergeMap((action) =>
      api.settings
        .getUserInfo(
          store.getState().partnerOptions.selectedCompany?.value,
          action.payload?.id || selectUserInfo(store.getState()).data.orgUserId
        )
        .map((response) => getUserInfoSuccess(response))
        .catch((error) => Observable.of(epicsErrorHandler(getUserInfoError, error)))
        .takeUntil(action$.ofType(GET_USER_INFO_ASYNC.CANCEL))
    )

const getManageableUserCompaniesEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$
    .ofType(
      GET_USER_MANAGEABLE_ASSIGNED_COMPANIES_ASYNC.INITIATE,
      GET_USER_MANAGEABLE_TO_ASSIGN_COMPANIES_ASYNC.INITIATE,
      CHANGE_ASSIGNED_COMPANIES_SEARCH_FILTER,
      CHANGE_ASSIGNED_COMPANIES_MSP_ROLE_FILTER,
      CHANGE_ASSIGNED_COMPANIES_PLAN_TYPE_FILTER,
      CHANGE_ALLOWED_MSP_ROLE_ID_FILTER,
      CHANGE_TO_ASSIGN_PLAN_TYPE_FILTER,
      CHANGE_TO_ASSIGN_COMPANIES_SEARCH_FILTER,
      ASSIGN_COMPANIES_ASYNC.SUCCESS,
      UNASSIGN_COMPANIES_ASYNC.SUCCESS
    )
    .switchMap((action) => {
      const isToAssignDrawerOpen = selectIsToAssignDrawerOpen(store.getState())
      const url = new URL(window.location)
      const pathSegments = url.pathname.split('/')
      const id = pathSegments[pathSegments.length - 1]
      let isAssigned = !isToAssignDrawerOpen

      if (action.type === ASSIGN_COMPANIES_ASYNC.SUCCESS) {
        isAssigned = true
      }

      const params = {
        id,
        companyId: store.getState().partnerOptions.selectedCompany?.value,
        pageSize: ASSIGNED_USER_COMPANIES_PER_PAGE,
        isAssigned,
      }

      const assignedParams = {
        ...params,
        page: selectAssignedCompaniesData(store.getState()).page,
        company: selectAssignedCompaniesSearchFilter(store.getState()),
        mspRoleIds: selectAssignedCompaniesMspRoleFilter(store.getState()),
        planTypes: selectAssignedCompaniesPLanTypeFilter(store.getState()),
      }
      const unAssignedParams = {
        ...params,
        page: selectToAssignCompaniesData(store.getState()).page,
        company: selectToAssignCompaniesSearchFilter(store.getState()),
        allowedMspRoleId: selectAllowedMspRoleIdFilter(store.getState()),
        planTypes: selectToAssignPlanTypeFilter(store.getState()),
      }

      const successAction = isAssigned
        ? getManageableUserAssignedCompaniesSuccess
        : getManageableUserToAssignCompaniesSuccess
      const errorAction = isAssigned
        ? getManageableUserAssignedCompaniesError
        : getManageableUserToAssignCompaniesError

      return api.settings
        .getManageableUserCompanies(isAssigned ? assignedParams : unAssignedParams)
        .map((response) => successAction(response))
        .catch((error) => Observable.of(epicsErrorHandler(errorAction, error)))
    })

const assignCompaniesEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(ASSIGN_COMPANIES_ASYNC.INITIATE).mergeMap(() => {
    const url = new URL(window.location)
    const pathSegments = url.pathname.split('/')
    const id = pathSegments[pathSegments.length - 1]
    const selectionData = selectToAssignSelectionData(store.getState())
    const data = {
      selectionData: selectionData.allItems
        ? {
            allItems: true,
            excludeIds: selectionData.selectionIds,
          }
        : {
            allItems: false,
            includeIds: selectionData.selectionIds,
            excludeIds: [],
          },
      mspRoleId: selectAllowedMspRoleIdFilter(store.getState()),
    }
    const planTypes = selectToAssignPlanTypeFilter(store.getState())
    const company = selectToAssignCompaniesSearchFilter(store.getState())

    if (planTypes.length) {
      data.planTypes = planTypes
    }

    if (company) {
      data.company = company
    }

    return api.settings
      .assignCompanies({
        id,
        companyId: store.getState().partnerOptions.selectedCompany?.value,
        data,
      })
      .mergeMap(() => {
        const toAssignCompaniesData = selectToAssignCompaniesData(store.getState())
        const count = selectionData.allItems
          ? toAssignCompaniesData.meta.count
          : selectionData.selectionIds.length

        return Observable.of(
          assignCompaniesSuccess(),
          addToast({
            message: `${count} ${
              selectionData.selectionIds.length > 1 ? 'companies' : 'company'
            } successfully assigned`,
          })
        )
      })
      .catch((error) =>
        Observable.of(
          epicsErrorHandler(assignCompaniesError, error),
          addToast({
            message: error.response?.message || error?.response,
          })
        )
      )
      .takeUntil(action$.ofType(ASSIGN_COMPANIES_ASYNC.CANCEL))
  })

const unassignCompaniesEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(UNASSIGN_COMPANIES_ASYNC.INITIATE).mergeMap(() => {
    const url = new URL(window.location)
    const pathSegments = url.pathname.split('/')
    const id = pathSegments[pathSegments.length - 1]
    const selectionData = selectAssignedSelectionData(store.getState())
    const data = {
      selectionData: selectionData.allItems
        ? {
            allItems: true,
            excludeIds: selectionData.selectionIds,
          }
        : {
            allItems: false,
            includeIds: selectionData.selectionIds,
            excludeIds: [],
          },
    }
    const company = selectAssignedCompaniesSearchFilter(store.getState())
    const mspRoleIds = selectAssignedCompaniesMspRoleFilter(store.getState())
    const planTypes = selectAssignedCompaniesPLanTypeFilter(store.getState())

    if (company) {
      data.company = company
    }

    if (mspRoleIds.length) {
      data.mspRoleIds = mspRoleIds
    }

    if (planTypes.length) {
      data.planTypes = planTypes
    }

    return api.settings
      .unassignMSPCompanies({
        id,
        companyId: store.getState().partnerOptions.selectedCompany?.value,
        data,
      })
      .mergeMap(() => {
        const assignedCompaniesData = selectAssignedCompaniesData(store.getState())
        const count = selectionData.allItems
          ? assignedCompaniesData.meta.count
          : selectionData.selectionIds.length

        return Observable.of(
          unassignMSPCompaniesSuccess(),
          addToast({
            message: `${count} ${
              selectionData.selectionIds.length > 1 ? 'companies' : 'company'
            } successfully unassigned`,
          })
        )
      })
      .catch((error) =>
        Observable.of(
          epicsErrorHandler(unassignMSPCompaniesError, error),
          addToast({
            message: error.response?.message || error?.response,
          })
        )
      )
      .takeUntil(action$.ofType(UNASSIGN_COMPANIES_ASYNC.CANCEL))
  })

export default combineEpics(
  getCompanyUsersEpic,
  getProfilesEpic,
  delCompanyUserEpic,
  addCompanyUserEpic,
  updCompanyUserEpic,
  makeOwnerEpic,
  fetchMailboxDataEpic,
  fetchCncAccessDataEpic,
  setCncAccessDataEpic,
  getManageableCompaniesEpic,
  getUserInfoEpic,
  getManageableUserCompaniesEpic,
  assignCompaniesEpic,
  unassignCompaniesEpic
)
